import { ClientHandler } from '../../../types';
import { CreativityType } from '../../../types/banner';
import type { FormDataOf } from '../../../types/templates';

import { getPlayerVsPlayerHelpers } from '../../../clients/DISNEY/espn/player-vs-player/helpers';
import { getESPNVSHelpers } from '../../../clients/DISNEY/espn/vs-unified/helpers';
import { getESPNHeroHelpers } from '../../../clients/DISNEY/espn/espn-heros/helpers';
import { getLogoVsLogoHelpers } from '../../../clients/DISNEY/espn/logo-vs-logo/helpers';
import { buildBannerType } from '../../../clients/DISNEY/espn/utils/build-banner-type';
import { disneyEspnHero } from '../../../clients/DISNEY/espn/espn-heros';
import { disneyEspnPlayerVSPlayer } from '../../../clients/DISNEY/espn/player-vs-player';
import { disneyEspnVS } from '../../../clients/DISNEY/espn/vs-unified';
import { disneyESPNLogoVsLogo } from '../../../clients/DISNEY/espn/logo-vs-logo';

type EspnHeroFormData = FormDataOf<typeof disneyEspnHero>;
type PlayerVsPlayerFormData = FormDataOf<typeof disneyEspnPlayerVSPlayer>;
type EspnVSFormData = FormDataOf<typeof disneyEspnVS>;
type LogoVsLogoFormData = FormDataOf<typeof disneyESPNLogoVsLogo>;

export const disneyHandler: ClientHandler = {
  transformBannerType: (formData, baseType) => {
    if (baseType === 'espn/espn-heros' || baseType === 'espn/logo-vs-logo') {
      return buildBannerType(formData, baseType) as CreativityType;
    }
    if (baseType === 'espn/espn-vs/futbol') {
      return getPlayerVsPlayerHelpers().buildBannerType(formData as PlayerVsPlayerFormData) as CreativityType;
    }
    if (baseType === 'espn/espn-vs') {
      return getESPNVSHelpers().buildESPNBannerType(formData as EspnVSFormData) as CreativityType;
    }
    return baseType;
  },

  computeDefaults: (formData, resolutionId) => {
    const creativityType = formData._creativityType;
    if (creativityType === 'espn/espn-heros') {
      return getESPNHeroHelpers().computeDefaults(formData as EspnHeroFormData, resolutionId);
    }
    if (creativityType === 'espn/logo-vs-logo') {
      return getLogoVsLogoHelpers().computeDefaults(formData as LogoVsLogoFormData, resolutionId) as Partial<
        typeof formData
      >;
    }
    return {} as Partial<typeof formData>;
  },

  applyFieldLogic: (fieldKey, value, formData) => {
    const creativityType = formData._creativityType;

    if (creativityType === 'espn/espn-heros') {
      return getESPNHeroHelpers().applyFieldLogic(fieldKey, value, formData as EspnHeroFormData);
    }
    if (creativityType === 'espn/espn-vs/futbol') {
      return getPlayerVsPlayerHelpers().applyFieldLogic(fieldKey, value, formData as PlayerVsPlayerFormData);
    }
    if (creativityType === 'espn/espn-vs') {
      return getESPNVSHelpers().applyFieldLogic(fieldKey, value, formData as EspnVSFormData);
    }
    if (creativityType === 'espn/logo-vs-logo') {
      return getLogoVsLogoHelpers().applyFieldLogic(fieldKey, value, formData as LogoVsLogoFormData);
    }

    return {};
  },

  cleanFormData: (formData, creativityType) => {
    if (creativityType.startsWith('espn/espn-heros')) {
      return getESPNHeroHelpers().cleanFormData(formData as EspnHeroFormData) as object as typeof formData;
    }
    if (creativityType === 'espn/espn-vs/futbol') {
      return getPlayerVsPlayerHelpers().cleanFormData(formData as PlayerVsPlayerFormData) as object as typeof formData;
    }
    if (creativityType === 'espn/espn-vs') {
      return getESPNVSHelpers().cleanFormData(formData as EspnVSFormData) as typeof formData;
    }

    const { title: _title, _creativityType: _ct, ...config } = formData;
    return config;
  },

  shouldShowField: (fieldKey, formData, creativityType) => {
    if (creativityType === 'espn/espn-vs') {
      return getESPNVSHelpers().shouldShowField(fieldKey, formData as EspnVSFormData);
    }
    return true;
  },

  getDynamicOptions: (fieldKey, formData, baseOptions, context) => {
    const creativityType = formData._creativityType;

    if (creativityType === 'espn/espn-heros') {
      return getESPNHeroHelpers().getDynamicOptions(fieldKey, formData as EspnHeroFormData, baseOptions);
    }
    if (creativityType === 'espn/espn-vs/futbol') {
      return getPlayerVsPlayerHelpers().getDynamicOptions(fieldKey, formData as PlayerVsPlayerFormData, baseOptions);
    }
    if (creativityType === 'espn/espn-vs') {
      return getESPNVSHelpers().getDynamicOptions(fieldKey, formData as EspnVSFormData, creativityType, baseOptions);
    }
    if (creativityType === 'espn/logo-vs-logo') {
      return getLogoVsLogoHelpers().getDynamicOptions(fieldKey, formData as LogoVsLogoFormData, baseOptions, context);
    }

    return baseOptions;
  },

  getFileNaming: (formData, resolution) => {
    const creativityType = formData._creativityType;
    if (creativityType?.startsWith('espn/espn-heros')) {
      return getESPNHeroHelpers().getFileNaming(formData as EspnHeroFormData, resolution);
    }
    if (creativityType?.startsWith('espn/logo-vs-logo')) {
      return getLogoVsLogoHelpers().getFileNaming(formData as LogoVsLogoFormData, resolution);
    }
    return undefined;
  },

  getBannerMetadata: banner => {
    if (!banner || !banner.bannerType) return null;
    if (banner.bannerType.startsWith('espn/espn-vs/')) {
      return getPlayerVsPlayerHelpers().getBannerMetadata(banner);
    }
    return null;
  },

  getBulkOperations: creativityType => {
    if (creativityType === 'espn/logo-vs-logo') {
      return getLogoVsLogoHelpers().bulkOperations;
    }
    return {};
  },

  buildFolder: (formData, creativityType) => {
    if (creativityType?.startsWith('espn/logo-vs-logo')) {
      const fd = formData as LogoVsLogoFormData;
      return getLogoVsLogoHelpers().buildFolder(fd.league as string, fd.matchday as string);
    }
    return undefined;
  }
};
